import { isIntentSwitchRemembered, rememberIntentSwitch } from './intentSwitchMemory';

// Single shared rule mapping a Cashier session intent to the application's
// two transaction type values and to the tab identifier used by the header.
//
// The backend's CashierIntent enum has three values: payment, authorization,
// withdrawal. This application only ever uses two transaction type values,
// 'pay-in' and 'pay-out' — an authorization is represented as a 'pay-in'
// transaction type plus the separate 'authorization' intent value, never as
// a third transaction type. Do not introduce a third value here.

export const CASHIER_INTENT = {
  PAYMENT: 'payment',
  AUTHORIZATION: 'authorization',
  WITHDRAWAL: 'withdrawal',
};

export const TRANSACTION_TYPE = {
  PAY_IN: 'pay-in',
  PAY_OUT: 'pay-out',
};

export const TAB = {
  DEPOSIT: 'deposit',
  WITHDRAWAL: 'withdrawal',
};

/**
 * The translation key each tab/label is looked up with. Both the modern tab
 * header and the classic header's transaction labels read the same word for
 * the same tab from here, so a multi-intent session shows identical wording
 * in either header style.
 */
export const TAB_LABEL_KEY: Record<string, string> = {
  [TAB.DEPOSIT]: 'deposit',
  [TAB.WITHDRAWAL]: 'withdraw',
};

const KNOWN_INTENTS = [
  CASHIER_INTENT.PAYMENT,
  CASHIER_INTENT.AUTHORIZATION,
  CASHIER_INTENT.WITHDRAWAL,
];

type Intent = string | null | undefined;

/**
 * True only for an intent value this application knows how to map.
 */
export const isKnownIntent = (intent: Intent): boolean =>
  KNOWN_INTENTS.indexOf(intent as string) !== -1;

/**
 * Maps a session intent to this application's transaction type.
 * Deposit covers both the payment intent and the authorization intent;
 * withdrawal covers the withdrawal intent. Any unrecognised/missing intent
 * falls back to the deposit transaction type, matching today's default.
 */
export const mapIntentToTransactionType = (intent: Intent): string =>
  intent === CASHIER_INTENT.WITHDRAWAL ? TRANSACTION_TYPE.PAY_OUT : TRANSACTION_TYPE.PAY_IN;

/**
 * The transaction type values of the gateway service's pay-with-gateway
 * submission. Unlike the application's own two values, these keep an
 * authorization apart from a plain deposit. They are sent only in the optional
 * `transaction_type` request field of that submission and are never written
 * into the application's own transaction type. The card routing check has its
 * own values (see CARD_ROUTING_TRANSACTION_TYPE).
 */
export const GATEWAY_TRANSACTION_TYPE = {
  PAY_IN: 'pay-in',
  PAY_IN_AUTHORIZE: 'pay-in-authorize',
  PAY_OUT: 'pay-out',
};

const GATEWAY_TRANSACTION_TYPE_BY_INTENT: Record<string, string> = {
  [CASHIER_INTENT.PAYMENT]: GATEWAY_TRANSACTION_TYPE.PAY_IN,
  [CASHIER_INTENT.AUTHORIZATION]: GATEWAY_TRANSACTION_TYPE.PAY_IN_AUTHORIZE,
  [CASHIER_INTENT.WITHDRAWAL]: GATEWAY_TRANSACTION_TYPE.PAY_OUT,
};

/**
 * Maps a session intent to the pay-with-gateway submission's transaction type
 * value: payment to pay-in, authorization to pay-in-authorize, withdrawal to pay-out.
 * An unknown or missing intent maps to no value at all, so no field is sent.
 */
export const mapIntentToGatewayTransactionType = (intent: Intent): string | undefined =>
  isKnownIntent(intent) ? GATEWAY_TRANSACTION_TYPE_BY_INTENT[intent as string] : undefined;

/**
 * Maps a session intent to the tab identifier shown/selected in the header.
 */
export const mapIntentToTab = (intent: Intent): string =>
  intent === CASHIER_INTENT.WITHDRAWAL ? TAB.WITHDRAWAL : TAB.DEPOSIT;

/**
 * The tab a session-wide transaction type stands for.
 *
 * The transaction type is itself derived from the active intent, so reading the
 * active tab from it follows a tab switch while staying identical to the rule
 * the screens already used, including for a session whose intent this
 * application does not recognise.
 */
export const mapTransactionTypeToTab = (transactionType: string | null | undefined): string =>
  transactionType === TRANSACTION_TYPE.PAY_OUT ? TAB.WITHDRAWAL : TAB.DEPOSIT;

/**
 * Resolves the session-wide transaction type from the currently active intent.
 *
 * An intent this application does not recognise carries no information, so the
 * transaction type the session reply carries is used unchanged. That keeps a
 * session whose intent is absent or unknown byte-identical to today.
 */
export const resolveTransactionType = (
  intent: Intent,
  sessionTransactionType: string | null | undefined,
): string | null | undefined =>
  isKnownIntent(intent) ? mapIntentToTransactionType(intent) : sessionTransactionType;

const NO_INTENTS: string[] = Object.freeze([]) as unknown as string[];

/**
 * The session's ordered intents list as stored at boot: the primary intent
 * first, the extra intents after it. A missing or malformed list reads as one
 * shared empty list, which means "no known intent".
 */
export const getStoredIntents = (state: any): string[] => {
  const intents = state?.initTheme?.sessionInformation?.intents;

  return Array.isArray(intents) ? intents : NO_INTENTS;
};

// One-slot cache: the rest of a stored list is computed once per list, so
// selectors that compare the result by reference (shallowEqual) do not see a
// new array on every store update.
let restCacheSource: string[] | null = null;
let restCacheValue: string[] = NO_INTENTS;

/**
 * The extra intents the session was created with: every element of the stored
 * list except the first. A single-element, empty or missing list all mean a
 * single-intent session. The same list always gives the same array.
 */
export const getSecondaryIntents = (state: any): string[] => {
  const intents = getStoredIntents(state);

  if (intents !== restCacheSource) {
    restCacheSource = intents;
    restCacheValue = intents.length > 1 ? intents.slice(1) : NO_INTENTS;
  }

  return restCacheValue;
};

/**
 * A session is multi-intent only when it carries at least one extra intent.
 */
export const isMultiIntentSession = (state: any): boolean => getSecondaryIntents(state).length > 0;

/**
 * The session's primary intent: the first element of the stored list.
 */
export const getPrimaryIntent = (state: any): Intent => getStoredIntents(state)[0];

/**
 * The intent of the tab the player is currently on. It starts as the primary
 * intent and only differs once the player switches tab, so a single-intent
 * session always resolves to the primary intent.
 */
export const getActiveIntent = (state: any): Intent =>
  state?.initTheme?.activeIntent ?? getPrimaryIntent(state);

/**
 * The tab the active intent stands for in a multi-intent session, or undefined
 * when that does not apply: a single-intent session, or an active intent this
 * application does not recognise.
 *
 * The active intent changes the moment the player clicks a tab, while the
 * customer profile only catches up once the new tab's data arrives, so the
 * selected tab must be read from the active intent. Screens that fall back to
 * their own rule when this is undefined stay exactly as they were for a
 * single-intent session.
 */
export const getActiveIntentTab = (state: any): string | undefined => {
  if (!isMultiIntentSession(state)) return undefined;

  const activeIntent = getActiveIntent(state);

  return isKnownIntent(activeIntent) ? mapIntentToTab(activeIntent) : undefined;
};

/**
 * The tab the player is on, everywhere it is shown or compared: the active
 * intent's tab in a multi-intent session, otherwise the tab the session-wide
 * transaction type stands for, which is today's rule.
 */
export const getActiveTab = (state: any): string =>
  getActiveIntentTab(state) ?? mapTransactionTypeToTab(state?.profileInfo?.transaction_type);

/**
 * The application's transaction type of the tab the player is on: the active
 * intent's type in a multi-intent session, otherwise the session-wide
 * transaction type held in the profile, unchanged.
 */
export const getActiveTransactionType = (state: any): string | null | undefined => {
  const profileTransactionType = state?.profileInfo?.transaction_type;

  if (!isMultiIntentSession(state)) return profileTransactionType;

  return resolveTransactionType(getActiveIntent(state), profileTransactionType);
};

/**
 * The transaction tabs stay locked from the click on a tab until the gateways
 * response of the new tab arrives (or no request can follow the switch), and
 * for the whole life of any other gateways request, so at most one request
 * runs at a time. A single-intent session has no switch to guard.
 */
export const areTransactionTabsLocked = (state: any): boolean =>
  isMultiIntentSession(state) &&
  (!!state?.tabSwitchAwaitingGateways || (state?.gatewaysRequestsInFlight ?? 0) > 0);

/**
 * Every intent the session was created with, primary first.
 */
export const getSessionIntents = (state: any): string[] =>
  [getPrimaryIntent(state), ...getSecondaryIntents(state)].filter(
    (intent): intent is string => typeof intent === 'string' && intent !== '',
  );

/**
 * True once the player has switched tab in this multi-intent session — in this
 * page, or before a reload of the same browser tab — or a reload reopened a
 * tab (see reopenIntent). Being on a secondary tab always counts as switched.
 *
 * This is the one rule every request builder consults: before the first
 * switch, and always in a single-intent session, nothing extra is sent.
 */
export const hasSwitchedInSession = (state: any): boolean => {
  if (!isMultiIntentSession(state)) return false;

  const activeIntent = getActiveIntent(state);

  if (isKnownIntent(activeIntent) && activeIntent !== getPrimaryIntent(state)) return true;

  return isIntentSwitchRemembered();
};

/**
 * The value of the `active_intent` request argument, or undefined when the
 * argument must not be sent at all.
 *
 * Before the player's first switch the argument is left out, so the backend
 * keeps falling back to the session's primary intent, which is exactly
 * today's behaviour; a single-intent session therefore never sends it. Once
 * the player has switched, the active tab's intent is sent on both tabs, the
 * primary one included, because a submission on the other tab may have
 * changed the transaction type the backend would otherwise fall back to.
 */
export const getActiveIntentArgument = (state: any): string | undefined => {
  if (!isMultiIntentSession(state)) return undefined;

  const activeIntent = getActiveIntent(state);

  if (!isKnownIntent(activeIntent)) return undefined;
  if (!hasSwitchedInSession(state)) return undefined;

  return activeIntent as string;
};

/**
 * The value of the optional `transaction_type` field of the pay-with-gateway
 * submission only, or undefined when the field must not be sent. Same rule as
 * the intent argument: only once the player has switched tab in a
 * multi-intent session, on both tabs. The card routing check uses
 * getCardRoutingTransactionTypeArgument, which has its own values.
 */
export const getGatewayTransactionTypeArgument = (state: any): string | undefined => {
  if (!hasSwitchedInSession(state)) return undefined;

  return mapIntentToGatewayTransactionType(getActiveIntent(state));
};

/**
 * The transaction type values the gateway service validator accepts for the
 * card routing check (`sale`, `payout`, `authorize`; the validator lives in
 * the uniongw-bridge service, not in this repository). They differ from the
 * pay-with-gateway submission's values. That the Sessions service passes the
 * value on to the validator is not verified here.
 */
export const CARD_ROUTING_TRANSACTION_TYPE = {
  SALE: 'sale',
  AUTHORIZE: 'authorize',
  PAYOUT: 'payout',
};

const CARD_ROUTING_TRANSACTION_TYPE_BY_INTENT: Record<string, string> = {
  [CASHIER_INTENT.PAYMENT]: CARD_ROUTING_TRANSACTION_TYPE.SALE,
  [CASHIER_INTENT.AUTHORIZATION]: CARD_ROUTING_TRANSACTION_TYPE.AUTHORIZE,
  [CASHIER_INTENT.WITHDRAWAL]: CARD_ROUTING_TRANSACTION_TYPE.PAYOUT,
};

/**
 * Maps a session intent to the card routing check's transaction type value:
 * payment to sale, authorization to authorize, withdrawal to payout. An
 * unknown or missing intent maps to no value at all, so no field is sent.
 */
export const mapIntentToCardRoutingTransactionType = (intent: Intent): string | undefined =>
  isKnownIntent(intent) ? CARD_ROUTING_TRANSACTION_TYPE_BY_INTENT[intent as string] : undefined;

/**
 * The value of the optional `transaction_type` field of the card routing
 * check, or undefined when the field must not be sent. Same sending rule as
 * the pay-with-gateway submission (only once the player has switched tab in a
 * multi-intent session, on both tabs), with the routing check's own values.
 */
export const getCardRoutingTransactionTypeArgument = (state: any): string | undefined => {
  if (!hasSwitchedInSession(state)) return undefined;

  return mapIntentToCardRoutingTransactionType(getActiveIntent(state));
};

/**
 * The session intent a gateway transaction type (as a returned transaction
 * carries it) belongs to, or undefined when the session has no such intent.
 * An exact match wins; otherwise a pay-in or pay-in-authorize value falls back
 * to whichever session intent the deposit tab stands for.
 */
export const mapGatewayTransactionTypeToIntent = (
  transactionType: string | null | undefined,
  sessionIntents: string[],
): string | undefined => {
  if (!transactionType) return undefined;

  const exact = sessionIntents.find(
    (intent) => mapIntentToGatewayTransactionType(intent) === transactionType,
  );
  if (exact) return exact;

  if (
    transactionType !== GATEWAY_TRANSACTION_TYPE.PAY_IN &&
    transactionType !== GATEWAY_TRANSACTION_TYPE.PAY_IN_AUTHORIZE
  )
    return undefined;

  return sessionIntents.find(
    (intent) => isKnownIntent(intent) && mapIntentToTab(intent) === TAB.DEPOSIT,
  );
};

/**
 * Reopens a tab after a reload that belongs to a transaction (decline
 * recovery, provider return, "try again"). Returns the intent to make active,
 * or undefined when `intent` is not one of this multi-intent session's
 * intents. A reopened secondary tab, or any tab once a switch was already
 * remembered, is remembered as switched so the requests keep carrying it.
 */
export const reopenIntent = (state: any, intent: Intent): string | undefined => {
  if (!isMultiIntentSession(state)) return undefined;
  if (!isKnownIntent(intent)) return undefined;
  if (getSessionIntents(state).indexOf(intent as string) === -1) return undefined;

  if (intent !== getPrimaryIntent(state) || isIntentSwitchRemembered()) {
    rememberIntentSwitch(intent as string);
  }

  return intent as string;
};
